// In-process mock router. Mimics the HTTP surface defined in API_CONTRACT.md
// so the feature api/client.ts can dispatch to it when USE_MOCK is true.

import type {
  CampaignConfig,
  CampaignState,
  ControlAction,
  SamplePoint,
  ScoreResponse,
  SurfaceResponse,
} from "../types";
import {
  campaignStore,
  cloneStateForFork,
  generateCampaignId,
} from "./campaignStore";
import {
  getOracleDataset,
  listOracleDatasets,
  lookup as oracleLookup,
} from "./oracleLoader";
import {
  addSamplerTranche,
  ensureTickLoopRunning,
  kickCampaign,
  recomputeMetrics,
} from "./wsSimulator";
import {
  fitAllOutputs,
  inferDivergenceFrontier,
  isAllowedByDivergenceFrontier,
  isAllowedBySymmetrySampling,
  normalisePoint,
  rangeAxes,
  scoreAt,
} from "./_mock_sampler";
import {
  canonicalTailorOutputName,
} from "./tailorMannequin";
import { predict } from "./surrogate";
import { fetchRealTailorAcquisition, fetchRealTailorSurface } from "../realTailorBridge";

export type MockRequestOptions = {
  method?: string;
  body?: unknown;
};

// Contract error envelope.
export class MockApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
  toEnvelope() {
    return { error: { code: this.code, message: this.message } };
  }
}

// ---------------------------------------------------------------------------
// Path parsing
// ---------------------------------------------------------------------------

type ParsedPath = {
  full: string;
  pathname: string;
  search: URLSearchParams;
};

function parsePath(path: string): ParsedPath {
  // Allow callers to pass a relative path like "/api/campaigns/abc/state?x=1".
  const url = new URL(path, "http://mock.local");
  return { full: path, pathname: url.pathname, search: url.searchParams };
}

function requireCampaign(id: string): CampaignState {
  const state = campaignStore.get(id);
  if (!state) {
    throw new MockApiError(
      404,
      "CAMPAIGN_NOT_FOUND",
      `No campaign exists with id ${id}.`,
    );
  }
  return state;
}

function snapshotState(state: CampaignState): CampaignState {
  return typeof structuredClone === "function"
    ? structuredClone(state)
    : JSON.parse(JSON.stringify(state)) as CampaignState;
}

function usesMannequin(state: CampaignState): boolean {
  return state.config.sampler?.engine === "tailor-mannequin" || state.config.sampler?.engine === "lhs-mannequin";
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

function validateConfig(config: CampaignConfig): void {
  if (!config || typeof config !== "object") {
    throw new MockApiError(400, "VALIDATION_ERROR", "Missing config body.");
  }
  if (!Array.isArray(config.inputs) || config.inputs.length === 0) {
    throw new MockApiError(400, "VALIDATION_ERROR", "config.inputs is required.");
  }
  if (!Array.isArray(config.outputs) || config.outputs.length === 0) {
    throw new MockApiError(400, "VALIDATION_ERROR", "config.outputs is required.");
  }
  if (!(config.budget >= 0)) {
    throw new MockApiError(400, "VALIDATION_ERROR", "config.budget must be non-negative.");
  }
  if (!(config.batchSize > 0)) {
    throw new MockApiError(400, "VALIDATION_ERROR", "config.batchSize must be > 0.");
  }
  for (const o of config.outputs) {
    if (!(o.weight >= 0)) {
      throw new MockApiError(400, "VALIDATION_ERROR", `Output ${o.name} has negative weight.`);
    }
  }
  for (const a of config.inputs) {
    if (a.mode === "range" && (a.min == null || a.max == null || a.min >= a.max)) {
      throw new MockApiError(400, "VALIDATION_ERROR", `Axis ${a.name} has invalid range.`);
    }
  }
  if (config.oracle?.enabled) {
    if (!config.oracle.datasetId || !getOracleDataset(config.oracle.datasetId)) {
      throw new MockApiError(
        404,
        "ORACLE_NOT_FOUND",
        `Oracle dataset ${config.oracle.datasetId ?? "<unset>"} is not registered.`,
      );
    }
  }
}

function assertInputInRange(
  config: CampaignConfig,
  inputs: Record<string, number>,
): void {
  const knownNames = new Set(config.inputs.map((a) => a.name));
  for (const [name, val] of Object.entries(inputs)) {
    if (!knownNames.has(name)) {
      throw new MockApiError(400, "VALIDATION_ERROR", `Unknown input ${name}.`);
    }
    if (!Number.isFinite(val)) {
      throw new MockApiError(400, "VALIDATION_ERROR", `Non-numeric value for ${name}.`);
    }
    const axis = config.inputs.find((a) => a.name === name);
    if (!axis) continue;
    if (axis.mode === "range" && ((axis.min != null && val < axis.min) || (axis.max != null && val > axis.max))) {
      throw new MockApiError(
        400,
        "INPUT_OUT_OF_RANGE",
        `Value ${val} for ${name} is outside [${axis.min}, ${axis.max}].`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

function handleListOracles(): unknown {
  return { oracles: listOracleDatasets() };
}

function handleListCampaigns(): unknown {
  const entries = campaignStore.all().map((s) => ({
    id: s.id,
    name: s.config.name,
    status: s.status,
    iteration: s.iteration,
    pointsComplete: s.pointsComplete,
    budget: s.config.budget,
    oracleEnabled: s.config.oracle?.enabled === true,
    oracleDatasetId: s.config.oracle?.datasetId,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  }));
  // Newest first.
  entries.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return { campaigns: entries };
}

async function handleCreateCampaign(body: unknown): Promise<unknown> {
  const config = body as CampaignConfig;
  validateConfig(config);
  const id = generateCampaignId();
  const state: CampaignState = {
    id,
    config,
    status: "configuring",
    iteration: 0,
    pointsComplete: 0,
    pointsInFlight: 0,
    points: [],
    metrics: {
      perOutput: Object.fromEntries(
        config.outputs.map((o) => [
          o.name,
          {
            maxSigmaByIter: [],
            meanSigmaByIter: [],
            ...(config.oracle?.enabled ? { realRmseByIter: [] as number[] } : {}),
            currentMaxSigma: 0,
            currentMeanSigma: 0,
            ...(config.oracle?.enabled ? { currentRealRmse: 0 } : {}),
            targetSigma: o.targetSigma,
            calibration: [],
          },
        ]),
      ),
      cumulativeCostFlexCredits: 0,
      autonomousRunCount: 0,
      manualRunCount: 0,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  campaignStore.set(state);
  // Transition immediately to running and kick the simulator.
  state.status = "running";
  campaignStore.set(state);
  await kickCampaign(state);
  ensureTickLoopRunning();
  return { id, state: snapshotState(state) };
}

function handleGetCampaignState(id: string): unknown {
  return snapshotState(requireCampaign(id));
}

async function handleNextBatch(id: string): Promise<unknown> {
  const state = requireCampaign(id);
  if (state.status !== "running") {
    throw new MockApiError(
      409,
      "ILLEGAL_STATE_TRANSITION",
      `Cannot request next-batch while status is ${state.status}.`,
    );
  }
  // Under mock mode, batches are already proposed automatically by the
  // simulator when the current batch completes. We honour the explicit call
  // by returning the current open batch if any, else proposing one now.
  const open = state.points.filter(
    (p) => p.status === "queued" && p.iter === state.iteration,
  );
  if (open.length > 0) {
    return { proposedPoints: open.map((p) => ({ ...p })) };
  }
  // Force a proposal in the simulator on the next tick by falling back to a
  // direct call through kickCampaign.
  const before = state.points.length;
  await kickCampaign(state);
  const after = state.points.slice(before);
  return { proposedPoints: after.map((p) => ({ ...p })) };
}

async function handleTranche(id: string, body: unknown): Promise<unknown> {
  const state = requireCampaign(id);
  const payload = body as { additionalBudget?: number };
  const additionalBudget = Math.max(0, Math.floor(payload?.additionalBudget ?? state.config.batchSize));
  const pointIds = await addSamplerTranche(state, additionalBudget);
  return { state: snapshotState(state), pointIds };
}

function handleObservations(
  id: string,
  body: unknown,
): unknown {
  const state = requireCampaign(id);
  if (state.status === "configuring" || state.status === "failed") {
    throw new MockApiError(
      409,
      "ILLEGAL_STATE_TRANSITION",
      `Cannot accept observations while status is ${state.status}.`,
    );
  }
  const payload = body as { points?: Array<Partial<SamplePoint> & { id: string; outputs: Record<string, number> }> };
  if (!payload?.points || !Array.isArray(payload.points)) {
    throw new MockApiError(400, "VALIDATION_ERROR", "Missing points[] in body.");
  }
  const knownOutputs = new Set(state.config.outputs.map((o) => o.name));

  // Validate first (all-or-nothing).
  for (const item of payload.points) {
    if (!item.id) {
      throw new MockApiError(400, "VALIDATION_ERROR", "Observation missing id.");
    }
    if (!item.outputs || typeof item.outputs !== "object") {
      throw new MockApiError(400, "VALIDATION_ERROR", `Observation ${item.id} missing outputs.`);
    }
    for (const [k, v] of Object.entries(item.outputs)) {
      if (!knownOutputs.has(k)) {
        throw new MockApiError(400, "VALIDATION_ERROR", `Unknown output ${k}.`);
      }
      if (!Number.isFinite(v)) {
        throw new MockApiError(400, "VALIDATION_ERROR", `Non-finite value for ${k}.`);
      }
    }
    const found = state.points.find((p) => p.id === item.id);
    if (!found) {
      throw new MockApiError(404, "POINT_NOT_FOUND", `Point ${item.id} not found.`);
    }
  }

  // Apply.
  for (const item of payload.points) {
    const p = state.points.find((x) => x.id === item.id)!;
    p.outputs = { ...p.outputs, ...item.outputs };
    if (item.truthOutputs) p.truthOutputs = item.truthOutputs;
    if (item.runtimeSec != null) p.runtimeSec = item.runtimeSec;
    if (item.costFlexCredits != null) p.costFlexCredits = item.costFlexCredits;
    if (item.flow360CaseUrl) p.flow360CaseUrl = item.flow360CaseUrl;
    if (p.status !== "done" && p.status !== "failed") {
      p.status = "done";
    }
  }
  recomputeMetrics(state);
  campaignStore.set(state);
  campaignStore.emit(id, { type: "metrics", campaignId: id, metrics: { ...state.metrics } });
  return { state: snapshotState(state) };
}

function handleManualSample(id: string, body: unknown): unknown {
  const state = requireCampaign(id);
  if (state.status === "done" || state.status === "failed") {
    throw new MockApiError(
      409,
      "ILLEGAL_STATE_TRANSITION",
      `Cannot add manual sample while status is ${state.status}.`,
    );
  }
  const payload = body as { inputs?: Record<string, number> };
  if (!payload?.inputs || typeof payload.inputs !== "object") {
    throw new MockApiError(400, "VALIDATION_ERROR", "Missing inputs object.");
  }
  assertInputInRange(state.config, payload.inputs);

  // Fill any missing range axes with midpoint and fixed axes with their value.
  const finalInputs: Record<string, number> = {};
  for (const axis of state.config.inputs) {
    if (axis.mode === "fixed" && axis.fixedValue != null) {
      finalInputs[axis.name] = axis.fixedValue;
    } else if (axis.mode === "range") {
      const v = payload.inputs[axis.name];
      if (v != null) finalInputs[axis.name] = v;
      else finalInputs[axis.name] = ((axis.min ?? 0) + (axis.max ?? 1)) / 2;
    }
  }

  const pid = campaignStore.nextPointId();
  const batchId = campaignStore.nextBatchId("manual");
  const point: SamplePoint = {
    id: pid,
    iter: state.iteration,
    batchId,
    status: "queued",
    source: "manual",
    inputs: finalInputs,
  };
  state.points.push(point);
  state.pointsInFlight += 1;
  campaignStore.set(state);
  campaignStore.emit(id, { type: "point", campaignId: id, point: { ...point } });
  campaignStore.emit(id, {
    type: "batch",
    campaignId: id,
    iteration: state.iteration,
    pointIds: [pid],
  });
  return { point };
}

function parseSlice(search: URLSearchParams): Record<string, number> {
  const raw = search.get("slice");
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    throw new MockApiError(400, "VALIDATION_ERROR", "Malformed slice JSON.");
  }
}

function pickAxesForSurface(
  state: CampaignState,
  search: URLSearchParams,
): { xAxis: string; yAxis: string } {
  const range = rangeAxes(state.config.inputs);
  const xInput = search.get("xInput") ?? range[0]?.name;
  const yInput = search.get("yInput") ?? range[1]?.name;
  if (!xInput || !yInput) {
    throw new MockApiError(400, "VALIDATION_ERROR", "xInput/yInput required.");
  }
  if (!state.config.inputs.some((a) => a.name === xInput)) {
    throw new MockApiError(400, "VALIDATION_ERROR", `Unknown xInput ${xInput}.`);
  }
  if (!state.config.inputs.some((a) => a.name === yInput)) {
    throw new MockApiError(400, "VALIDATION_ERROR", `Unknown yInput ${yInput}.`);
  }
  return { xAxis: xInput, yAxis: yInput };
}

function parseResolution(search: URLSearchParams): number {
  const raw = search.get("resolution");
  const n = raw ? parseInt(raw, 10) : 60;
  if (!Number.isFinite(n) || n < 16 || n > 256) {
    throw new MockApiError(400, "VALIDATION_ERROR", "resolution out of bounds.");
  }
  return n;
}

function buildGridInputs(
  state: CampaignState,
  xAxis: string,
  yAxis: string,
  slice: Record<string, number>,
  xi: number,
  yj: number,
): Record<string, number> {
  const inputs: Record<string, number> = { [xAxis]: xi, [yAxis]: yj };
  for (const axis of state.config.inputs) {
    if (axis.name === xAxis || axis.name === yAxis) continue;
    if (slice[axis.name] != null) {
      inputs[axis.name] = slice[axis.name] ?? 0;
    } else if (axis.mode === "fixed" && axis.fixedValue != null) {
      inputs[axis.name] = axis.fixedValue;
    } else {
      inputs[axis.name] = ((axis.min ?? 0) + (axis.max ?? 1)) / 2;
    }
  }
  return inputs;
}

function resolveOutputName(state: CampaignState, requested: string): string | null {
  const canonicalRequested = canonicalTailorOutputName(requested);
  return state.config.outputs.find((output) =>
    output.name === requested || canonicalTailorOutputName(output.name) === canonicalRequested
  )?.name ?? null;
}

async function handleSurface(id: string, search: URLSearchParams, signal?: AbortSignal): Promise<SurfaceResponse> {
  const state = requireCampaign(id);
  const requestedOutput = search.get("output");
  if (!requestedOutput) {
    throw new MockApiError(400, "VALIDATION_ERROR", "output query param required.");
  }
  const output = resolveOutputName(state, requestedOutput);
  if (!output) {
    throw new MockApiError(400, "VALIDATION_ERROR", `Unknown output ${requestedOutput}.`);
  }
  const { xAxis, yAxis } = pickAxesForSurface(state, search);
  const slice = parseSlice(search);
  const resolution = parseResolution(search);
  const requestedTransportAxes = search.get("transportAxes")
    ?.split(",")
    .map((axis) => axis.trim())
    .filter((axis) => state.config.inputs.some((input) => input.name === axis));
  const rawSelectionHeld = search.get("selectionHeld");
  let selectionHeld: Record<string, number> | undefined;
  if (rawSelectionHeld) {
    try {
      selectionHeld = JSON.parse(rawSelectionHeld);
    } catch {
      throw new MockApiError(400, "VALIDATION_ERROR", "Malformed selectionHeld JSON.");
    }
  }

  const range = rangeAxes(state.config.inputs);
  const xSpec = state.config.inputs.find((a) => a.name === xAxis)!;
  const ySpec = state.config.inputs.find((a) => a.name === yAxis)!;
  const xMin = xSpec.min ?? 0;
  const xMax = xSpec.max ?? 1;
  const yMin = ySpec.min ?? 0;
  const yMax = ySpec.max ?? 1;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < resolution; i++) xs.push(xMin + ((xMax - xMin) * i) / (resolution - 1));
  for (let j = 0; j < resolution; j++) ys.push(yMin + ((yMax - yMin) * j) / (resolution - 1));

  const requestedPointLimit = Number(search.get("pointLimit"));
  const pointLimit = Number.isFinite(requestedPointLimit) && requestedPointLimit > 0
    ? Math.max(1, Math.trunc(requestedPointLimit))
    : undefined;
  const completed = state.points
    .filter((point) => point.status === "done" && point.outputs)
    .sort((left, right) => left.iter - right.iter || left.id.localeCompare(right.id));
  const observed = pointLimit == null ? completed : completed.slice(0, pointLimit);
  if (usesMannequin(state)) {
    const response = await fetchRealTailorSurface(
      state,
      observed,
      output,
      xAxis,
      yAxis,
      slice,
      resolution,
      requestedTransportAxes,
      selectionHeld,
      search.get("transportMonteCarlo") === "1",
      Number(search.get("transportMcMembers")) || undefined,
      signal,
    );
    campaignStore.set(state);
    return {
      x: response.x,
      y: response.y,
      mean: response.mean,
      sigma: response.sigma,
      priorMean: response.prior_mean,
      truthMean: response.truth_mean,
      totalTransportObservations: response.total_transport_observations,
      returnedTransportObservations: response.returned_transport_observations,
      transportMc: response.transport_mc && {
        members: response.transport_mc.members,
        rowFraction: response.transport_mc.row_fraction,
        lengthscaleJitter: response.transport_mc.lengthscale_jitter,
        method: response.transport_mc.method,
      },
      transport: Object.fromEntries(response.transport.flatMap((item) => item.id ? [[item.id, {
        mean: item.mean,
        sigma: item.sigma,
        correlation: item.correlation,
        hiddenDistance: item.hidden_distance,
        hiddenMaxDelta: item.hidden_max_delta,
        oracleMean: item.oracle_mean,
        mc: item.mc,
        mcSigma: item.mc_sigma,
      }]] : [])),
    };
  }
  const models = fitAllOutputs(state.config.inputs, state.config.outputs, observed);
  const model = models.find((m) => m.name === output)?.model;
  if (!model) {
    throw new MockApiError(400, "VALIDATION_ERROR", `Unknown output ${requestedOutput}.`);
  }

  const mean: number[][] = [];
  const sigma: number[][] = [];
  const truthMean: number[][] | undefined = state.config.oracle?.enabled ? [] : undefined;

  for (let j = 0; j < resolution; j++) {
    const meanRow: number[] = [];
    const sigmaRow: number[] = [];
    const truthRow: number[] | undefined = truthMean ? [] : undefined;
    const yv = ys[j] ?? 0;
    for (let i = 0; i < resolution; i++) {
      const xv = xs[i] ?? 0;
      const inputs = buildGridInputs(state, xAxis, yAxis, slice, xv, yv);
      const xn = normalisePoint(inputs, range);
      const pred = predict(model, xn);
      meanRow.push(pred.mean);
      sigmaRow.push(pred.sigma);
      if (truthRow && state.config.oracle?.datasetId) {
        const t = oracleLookup(state.config.oracle.datasetId, inputs);
        const tv = t.ok ? t.outputs[output] : undefined;
        truthRow.push(tv != null ? tv : NaN);
      }
    }
    mean.push(meanRow);
    sigma.push(sigmaRow);
    if (truthRow && truthMean) truthMean.push(truthRow);
  }

  return { x: xs, y: ys, mean, sigma, truthMean };
}

async function handleScore(id: string, search: URLSearchParams): Promise<ScoreResponse> {
  const state = requireCampaign(id);
  const { xAxis, yAxis } = pickAxesForSurface(state, search);
  const slice = parseSlice(search);
  const resolution = parseResolution(search);
  const disabledRaw = search.getAll("disabledOutputs").flatMap((s) => s.split(","));
  const disabled = new Set(disabledRaw.filter(Boolean));

  const knownOutputs = new Set(state.config.outputs.map((o) => o.name));
  for (const d of disabled) {
    if (!knownOutputs.has(d)) {
      throw new MockApiError(400, "VALIDATION_ERROR", `Unknown disabled output ${d}.`);
    }
  }

  const range = rangeAxes(state.config.inputs);
  const xSpec = state.config.inputs.find((a) => a.name === xAxis)!;
  const ySpec = state.config.inputs.find((a) => a.name === yAxis)!;
  const xMin = xSpec.min ?? 0;
  const xMax = xSpec.max ?? 1;
  const yMin = ySpec.min ?? 0;
  const yMax = ySpec.max ?? 1;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < resolution; i++) xs.push(xMin + ((xMax - xMin) * i) / (resolution - 1));
  for (let j = 0; j < resolution; j++) ys.push(yMin + ((yMax - yMin) * j) / (resolution - 1));

  const observed = state.points.filter((p) => p.status === "done" && p.outputs);
  if (state.config.sampler?.engine === "tailor-mannequin") {
    const response = await fetchRealTailorAcquisition(
      state,
      observed,
      xAxis,
      yAxis,
      slice,
      resolution,
    );
    const cells = response.score.flatMap((row, yIndex) => row.map((score, xIndex) => ({
      x: response.x[xIndex] ?? 0,
      y: response.y[yIndex] ?? 0,
      score,
    })));
    cells.sort((left, right) => right.score - left.score);
    return {
      x: response.x,
      y: response.y,
      score: response.score,
      topK: cells.slice(0, state.config.batchSize).map((cell, index) => ({
        x: cell.x,
        y: cell.y,
        rank: index + 1,
      })),
    };
  }
  const perOutput = fitAllOutputs(state.config.inputs, state.config.outputs, observed);
  const divergenceFrontier = inferDivergenceFrontier(
    state.config.inputs,
    state.config.outputs,
    observed,
    state.config.postDivergenceSampling,
  );
  // Rough max-|mu| per output, sampled at the grid centre row/column.
  const maxAbsMu: Record<string, number> = {};
  for (const { name, model } of perOutput) {
    let m = 1e-6;
    for (const xi of xs) {
      const inputs = buildGridInputs(state, xAxis, yAxis, slice, xi, (yMin + yMax) / 2);
      const { mean } = predict(model, normalisePoint(inputs, range));
      if (Math.abs(mean) > m) m = Math.abs(mean);
    }
    maxAbsMu[name] = m;
  }

  const scores: number[][] = [];
  let globalMin = Infinity;
  let globalMax = -Infinity;
  for (let j = 0; j < resolution; j++) {
    const row: number[] = [];
    const yv = ys[j] ?? 0;
    for (let i = 0; i < resolution; i++) {
      const xv = xs[i] ?? 0;
      const inputs = buildGridInputs(state, xAxis, yAxis, slice, xv, yv);
      if (!isAllowedByDivergenceFrontier(inputs, divergenceFrontier)) {
        row.push(0);
        if (0 < globalMin) globalMin = 0;
        if (0 > globalMax) globalMax = 0;
        continue;
      }
      if (!isAllowedBySymmetrySampling(
        inputs,
        state.config.inputs,
        state.config.sampler?.symmetries ?? state.config.sampler?.symmetry,
      )) {
        row.push(0);
        if (0 < globalMin) globalMin = 0;
        if (0 > globalMax) globalMax = 0;
        continue;
      }
      const xn = normalisePoint(inputs, range);
      const s = scoreAt(xn, perOutput, maxAbsMu, disabled);
      row.push(s);
      if (s < globalMin) globalMin = s;
      if (s > globalMax) globalMax = s;
    }
    scores.push(row);
  }

  // Min-max normalise to [0, 1].
  const span = globalMax - globalMin || 1;
  const normScores = scores.map((row) => row.map((v) => (v - globalMin) / span));

  // Build topK (up to batchSize). Collect all cells, sort by score desc.
  const cells: Array<{ x: number; y: number; score: number }> = [];
  for (let j = 0; j < resolution; j++) {
    const row = scores[j] ?? [];
    const yv = ys[j] ?? 0;
    for (let i = 0; i < resolution; i++) {
      cells.push({ x: xs[i] ?? 0, y: yv, score: row[i] ?? 0 });
    }
  }
  cells.sort((a, b) => b.score - a.score);
  const topKRaw = cells.slice(0, state.config.batchSize);
  const topK = topKRaw.map((c, idx) => ({ x: c.x, y: c.y, rank: idx + 1 }));

  return { x: xs, y: ys, score: normScores, topK };
}

async function handleControl(id: string, body: unknown): Promise<unknown> {
  const state = requireCampaign(id);
  const payload = body as { action?: ControlAction };
  const action = payload?.action;
  if (action !== "pause" && action !== "resume" && action !== "stop") {
    throw new MockApiError(400, "VALIDATION_ERROR", "action must be pause/resume/stop.");
  }
  const from = state.status;

  const transition = (next: CampaignState["status"]) => {
    state.status = next;
    campaignStore.set(state);
    campaignStore.emit(id, { type: "state", campaignId: id, state });
    if (next === "done") {
      campaignStore.emit(id, { type: "completed", campaignId: id });
    }
  };

  if (action === "pause") {
    if (from === "running") {
      transition("paused");
    } else {
      throw new MockApiError(409, "ILLEGAL_STATE_TRANSITION", `Cannot pause from ${from}.`);
    }
  } else if (action === "resume") {
    if (from === "configuring" || from === "paused") {
      state.status = "running";
      campaignStore.set(state);
      await kickCampaign(state);
      campaignStore.emit(id, { type: "state", campaignId: id, state });
    } else {
      throw new MockApiError(409, "ILLEGAL_STATE_TRANSITION", `Cannot resume from ${from}.`);
    }
  } else {
    // stop
    if (from === "done" || from === "failed") {
      throw new MockApiError(409, "ILLEGAL_STATE_TRANSITION", `Cannot stop from ${from}.`);
    }
    transition("done");
  }
  return { state: snapshotState(state) };
}

function handleFork(id: string, body: unknown): unknown {
  const source = requireCampaign(id);
  const payload = body as { atIteration?: number; overrides?: Partial<CampaignConfig> };
  const atIteration = payload?.atIteration;
  if (atIteration == null || !Number.isInteger(atIteration) || atIteration < 0) {
    throw new MockApiError(400, "VALIDATION_ERROR", "atIteration must be a non-negative integer.");
  }
  if (atIteration > source.iteration) {
    throw new MockApiError(409, "ITERATION_OUT_OF_BOUNDS", "atIteration exceeds source progress.");
  }
  if (payload.overrides) {
    // Validate merged config.
    const merged = { ...source.config, ...payload.overrides };
    validateConfig(merged as CampaignConfig);
  }
  const newId = generateCampaignId();
  const forked = cloneStateForFork(source, newId, atIteration, payload.overrides);
  campaignStore.set(forked);
  campaignStore.recordFork({ fromId: id, toId: newId, atIteration });
  // Fork lands in configuring; caller can POST control{resume}.
  return { id: newId, state: forked };
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

const CAMPAIGN_RE = /^\/api\/campaigns\/([^/]+)(?:\/(.+))?$/;

export async function mockRequest<T = unknown>(
  path: string,
  method: string = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const m = method.toUpperCase();
  try {
    if (signal?.aborted) throw signal.reason ?? new DOMException("Request aborted", "AbortError");
    const { pathname, search } = parsePath(path);

    if (pathname === "/api/oracles" && m === "GET") {
      return handleListOracles() as T;
    }
    if (pathname === "/api/campaigns" && m === "GET") {
      return handleListCampaigns() as T;
    }
    if (pathname === "/api/campaigns" && m === "POST") {
      return await handleCreateCampaign(body) as T;
    }
    const match = pathname.match(CAMPAIGN_RE);
    if (match) {
      const id = match[1] ?? "";
      const sub = match[2];
      if (!sub && m === "GET") {
        return requireCampaign(id) as unknown as T;
      }
      if (sub === "state" && m === "GET") {
        return handleGetCampaignState(id) as T;
      }
      if (sub === "next-batch" && m === "POST") {
        return await handleNextBatch(id) as T;
      }
      if (sub === "tranche" && m === "POST") {
        return await handleTranche(id, body) as T;
      }
      if (sub === "observations" && m === "POST") {
        return handleObservations(id, body) as T;
      }
      if (sub === "manual-sample" && m === "POST") {
        return handleManualSample(id, body) as T;
      }
      if (sub === "surface" && m === "GET") {
        return await handleSurface(id, search, signal) as unknown as T;
      }
      if (sub === "score" && m === "GET") {
        return await handleScore(id, search) as unknown as T;
      }
      if (sub === "control" && m === "POST") {
        return await handleControl(id, body) as T;
      }
      if (sub === "fork" && m === "POST") {
        return handleFork(id, body) as T;
      }
    }
    throw new MockApiError(404, "NOT_FOUND", `No mock handler for ${m} ${pathname}.`);
  } catch (err) {
    if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) throw err;
    if (err instanceof MockApiError) throw err;
    const msg = err instanceof Error ? err.message : String(err);
    throw new MockApiError(500, "MOCK_INTERNAL", msg);
  }
}

// Re-export for consumers who want direct access to the store / fork ledger.
export { campaignStore };
